import { useCallback, useEffect, useState } from 'react';
import {
  readDismissedTier,
  writeDismissedTier,
  type UsageTier,
} from '@/lib/usage-alert';

/**
 * Per-tier, per-org banner dismissal backed by localStorage.
 *
 * Dismissing records the current tier; `dismissed` stays true only while the
 * live tier is at or below what was dismissed. So a banner dismissed at 75%
 * re-appears when usage climbs into 90% or frozen (100%). When usage falls
 * back below the alert floor (tier 0, e.g. a new billing month) the stored
 * tier is cleared so a stale dismissal never suppresses a fresh cycle.
 *
 * The bell notification deliberately does NOT use this — it persists while
 * usage is high regardless of banner dismissal.
 */
export function useDismissibleUsageAlert(orgId: string | null, tier: UsageTier) {
  const [dismissedTier, setDismissedTier] = useState<UsageTier>(() =>
    orgId ? readDismissedTier(orgId) : 0,
  );
  const [prevOrgId, setPrevOrgId] = useState(orgId);

  // Re-sync from storage when the resolved org changes (it loads async). Adjust
  // state during render via the "storing information from previous renders"
  // pattern (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // rather than in an effect — avoids the set-state-in-effect render cascade.
  if (orgId !== prevOrgId) {
    setPrevOrgId(orgId);
    setDismissedTier(orgId ? readDismissedTier(orgId) : 0);
  }

  // Clear a stale dismissal once usage drops back below the alert floor (e.g. a
  // new billing month) so it never suppresses a fresh cycle. In-memory reset is
  // derived during render; the storage write is the genuine external-system
  // side effect (idempotent — writing 0 when already 0 is a no-op).
  if (tier === 0 && dismissedTier !== 0) {
    setDismissedTier(0);
  }
  useEffect(() => {
    if (orgId && tier === 0) {
      writeDismissedTier(orgId, 0);
    }
  }, [orgId, tier]);

  const dismissed = tier !== 0 && dismissedTier >= tier;

  const dismiss = useCallback(() => {
    if (!orgId || tier === 0) return;
    writeDismissedTier(orgId, tier);
    setDismissedTier(tier);
  }, [orgId, tier]);

  return { dismissed, dismiss };
}
